/**
 * Core balance configuration
 * All tunable game values in one place for easy balancing
 */

export const BALANCE_CONFIG = {
  // === FISHING ===
  BASE_FISHING_TIME: 3.0, // Seconds per fishing attempt
  MIN_FISHING_TIME: 0.5, // Floor (with max upgrades)
  BASE_CATCH_CHANCE: 0.6, // 60% base success rate
  MAX_CATCH_CHANCE: 0.95, // 95% cap (always some failure)

  // === INVENTORY ===
  STARTING_INVENTORY_CAPACITY: 20,
  INVENTORY_EXPANSION_COST: 100, // Future: upgrade capacity
  INVENTORY_EXPANSION_INCREASE: 10,

  // === ECONOMY ===
  STARTING_CURRENCY: 0,
  FIRST_UPGRADE_TARGET_TIME: 180, // 3 minutes to first upgrade (in seconds)
  CURRENCY_FORMAT_THRESHOLD_K: 1000,
  CURRENCY_FORMAT_THRESHOLD_M: 1000000,

  // === BUDDIES (Legacy complex system) ===
  ROD_BUDDY_UNLOCK_COST: 150,
  ROD_BUDDY_FISHING_TIME_MULT: 1.5, // 50% slower than player
  ROD_BUDDY_CATCH_REDUCTION: 0.15, // -15% catch chance
  ROD_BUDDY_QUALITY_CAP: 'good', // Can't catch Excellent

  RUNNER_BUDDY_UNLOCK_COST: 200,
  RUNNER_BUDDY_RUN_INTERVAL: 15, // Runs every 15 seconds
  RUNNER_BUDDY_PRICE_MULT: 1.0, // Sells for full price
  RUNNER_BUDDY_MAX_QUALITY: 'good', // Only sells up to Good quality

  // === FISHING BUDDIES (Phase 1 - simplified auto-fishing) ===
  FISHING_BUDDY_COSTS: [100, 250, 500], // Progressive costs for buddies 1, 2, 3
  FISHING_BUDDY_INTERVAL: 6, // Seconds between auto-catches
  FISHING_BUDDY_FISHING_TIME_MULT: 1.2, // 20% slower than player
  FISHING_BUDDY_CATCH_REDUCTION: 0.1, // -10% catch chance

  // === HAULER BUDDIES (Phase 1 - simplified auto-selling) ===
  HAULER_BUDDY_COSTS: [500, 1250], // More expensive than fishing buddies (only 2 slots)
  HAULER_BASE_TRAVEL_TIME: 30, // Seconds for round trip (break-even with fishing)
  HAULER_BASE_CAPACITY: 5, // Fish per trip
  HAULER_CAPACITY_PER_PHYSIQUE: 0.5, // +1 fish per 2 physique points
  HAULER_LOADING_TIME: 2, // Seconds to load fish
  HAULER_SELLING_TIME: 3, // Seconds at buyer
  HAULER_XP_PER_GOLD: 0.1, // 1 XP per 10 gold earned
  // Stat effects
  HAULER_SPEED_PER_EYESIGHT: 0.015, // 1.5% faster per eyesight point

  // === TRAPS ===
  TRAP_COOLDOWN_MS: 30 * 1000,    // 30 second cooldown between pulls
  TRAP_BAIT_YIELD_BONUS_MIN: 1,   // Min extra creatures when baited
  TRAP_BAIT_YIELD_BONUS_MAX: 2,   // Max extra creatures when baited
  TRAP_BAIT_QUALITY_BONUS: 0.05,  // Base quality bonus from using any trap bait

  // === SAVE SYSTEM ===
  AUTOSAVE_INTERVAL: 30, // Seconds between autosaves
  SAVE_VERSION: '1.0.0',

  // === FLY CASTING ===
  FLY_CAST_PERFECT_CATCH_BONUS: 0.30,    // +30% catch chance on Perfect
  FLY_CAST_PERFECT_QUALITY_BONUS: 0.25,  // +25% quality bonus on Perfect
  FLY_CAST_GOOD_CATCH_BONUS: 0.15,       // +15% catch chance on Good
  FLY_CAST_GOOD_QUALITY_BONUS: 0.10,     // +10% quality bonus on Good
  FLY_CAST_MISS_CATCH_BONUS: 0.05,       // +5% catch chance on Miss (participation)
  FLY_CAST_MISS_QUALITY_BONUS: 0.0,      // No quality bonus on Miss

  // Accuracy thresholds
  FLY_CAST_PERFECT_THRESHOLD: 0.85,      // 85%+ accuracy = Perfect
  FLY_CAST_GOOD_THRESHOLD: 0.60,         // 60-84% accuracy = Good
  // Below 60% = Miss

  // Curve generation
  FLY_CAST_MIN_CONTROL_POINTS: 3,
  FLY_CAST_MAX_CONTROL_POINTS: 5,
  FLY_CAST_CIRCLE_RADIUS: 140,           // SVG circle radius in px
  FLY_CAST_TIMEOUT_MS: 8000,             // Max time to complete trace

  // Species targeting
  FLY_CAST_PERFECT_RARITY_BOOST: 2.5,    // Multiply rare+ weights on Perfect
  FLY_CAST_GOOD_RARITY_BOOST: 1.5,       // Multiply rare+ weights on Good

  // Fly consumption
  FLY_LOSS_CHANCE_ON_MISS: 0.70,         // 70% chance to lose fly on miss (<60% accuracy)

  // Trace tolerance
  FLY_CAST_PERFECT_TOLERANCE: 15,        // Pixels — within this = green trail
  FLY_CAST_GOOD_TOLERANCE: 35,           // Pixels — within this = yellow trail
  FLY_CAST_START_TOLERANCE: 30,          // Pixels — must start within this of start point

  // === WORM DIG ===
  WORM_DIG_MIN_WORMS: 2,                    // Minimum worms per session
  WORM_DIG_MAX_WORMS: 5,                    // Maximum worms per session
  WORM_DIG_REVEAL_THRESHOLD: 0.55,          // 55% of zone cleared to reveal worm
  WORM_DIG_BRUSH_RADIUS: 28,               // Dig brush radius in px
  WORM_DIG_WORM_HIT_RADIUS: 48,            // Detection zone radius per worm in px
  WORM_DIG_TIMEOUT_MS: 30000,              // 30s max duration
  WORM_DIG_BAIT_PER_WORM: 2,              // Bait units awarded per worm found
  WORM_DIG_MIN_SPACING: 100,              // Min px between worm centers
  WORM_DIG_EDGE_PADDING: 60,              // Min px from container edges
  WORM_DIG_MAX_PARTICLES: 30,             // Concurrent dirt particle cap
  WORM_DIG_PARTICLE_SPAWN_THROTTLE: 60,   // ms between particle spawns

  // === UI/UX ===
  NOTIFICATION_DURATION: 3000, // Toast duration (ms)
  ANIMATION_DURATION: 300, // Standard animation (ms)
  DEBOUNCE_DELAY: 500, // Input debounce (ms)
} as const;

// Formatting utilities moved to src/utils/formatting.ts — re-exported for backward compatibility
export { formatCurrency, formatTime } from '@/utils/formatting';
